import React, { useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { FaApple, FaGooglePlay, FaGithub, FaCheckCircle, FaTimes, FaLayerGroup, FaCode, FaRocket, FaUserCheck, FaGlobe } from "react-icons/fa";
import { PhoneMockup, BrowserMockup, TerminalMockup, CodePackageMockup } from "../DeviceMockup";

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!project) return null;

  const isWideMockup = project.mockupType === "browser" || project.mockupType === "terminal" || project.mockupType === "code";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="project-modal-backdrop" onClick={onClose}>
          <motion.div
            className={`project-modal-content ${isWideMockup ? "wide-layout-modal" : "split-layout-modal"}`}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95, y: 25 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 25 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {/* Close Button */}
            <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
              <FaTimes />
            </button>

            {/* If Wide Mockup (Desktop / Browser / Terminal / Code), stack cleanly */}
            {isWideMockup ? (
              <div className="modal-wide-wrapper">
                {/* Header Meta */}
                <div className="modal-header-meta">
                  <span
                    className="modal-badge-cat"
                    style={{
                      color: project.color,
                      borderColor: `${project.color}50`,
                      backgroundColor: `${project.color}15`,
                    }}
                  >
                    {project.categoryLabel} • {project.platform}
                  </span>
                  <span className="modal-badge-status">
                    <span className="status-dot"></span> {project.badge}
                  </span>
                </div>

                <h2 className="modal-title">{project.title}</h2>
                <p className="modal-subtitle">{project.subtitle}</p>

                {project.client && (
                  <div className="modal-client-tag">
                    <FaUserCheck className="client-icon" /> Client: <strong>{project.client}</strong>
                  </div>
                )}

                {/* Wide Visual Showcase */}
                <div className="modal-visual-wide-banner">
                  {project.mockupType === "browser" && <BrowserMockup project={project} />}
                  {project.mockupType === "terminal" && <TerminalMockup project={project} />}
                  {project.mockupType === "code" && <CodePackageMockup project={project} />}
                </div>

                {/* Details Grid */}
                <div className="modal-wide-details-grid">
                  <div className="modal-details-col">
                    <div className="modal-section">
                      <h4 className="section-heading">
                        <FaLayerGroup /> Project Overview
                      </h4>
                      <p className="modal-description">{project.longDesc}</p>
                    </div>

                    <div className="modal-section">
                      <h4 className="section-heading">
                        <FaCode /> Technology Stack
                      </h4>
                      <div className="modal-tags-row">
                        {project.tags.map((tag, idx) => (
                          <span key={idx} className="modal-tech-chip">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="modal-details-col">
                    <div className="modal-section">
                      <h4 className="section-heading">
                        <FaRocket /> Engineering Highlights
                      </h4>
                      <ul className="modal-highlights-list">
                        {project.highlights.map((highlight, index) => (
                          <li key={index}>
                            <FaCheckCircle className="highlight-check" style={{ color: project.color }} />
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Actions Footer */}
                <div className="modal-actions-footer">
                  {project.links.web && (
                    <a
                      href={project.links.web}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-action-btn web"
                    >
                      <FaGlobe className="store-btn-icon" />
                      <div className="store-btn-text">
                        <small>Visit Platform</small>
                        <strong>Live Website</strong>
                      </div>
                    </a>
                  )}

                  {project.links.appStore && (
                    <a
                      href={project.links.appStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-action-btn appstore"
                    >
                      <FaApple className="store-btn-icon" />
                      <div className="store-btn-text">
                        <small>Download on the</small>
                        <strong>App Store</strong>
                      </div>
                    </a>
                  )}

                  {project.links.playStore && (
                    <a
                      href={project.links.playStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-action-btn playstore"
                    >
                      <FaGooglePlay className="store-btn-icon" />
                      <div className="store-btn-text">
                        <small>Get it on</small>
                        <strong>Google Play</strong>
                      </div>
                    </a>
                  )}

                  {project.links.github && (
                    <a
                      href={project.links.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="store-action-btn github"
                    >
                      <FaGithub className="store-btn-icon" />
                      <div className="store-btn-text">
                        <small>View Code</small>
                        <strong>GitHub Repository</strong>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            ) : (
              /* Split Layout for Mobile Phone Mockups */
              <div className="modal-body-grid">
                {/* Left Column: Visual Mockup Showcase */}
                <div className="modal-visual-column">
                  <div className="modal-mockup-wrapper">
                    <PhoneMockup project={project} />
                  </div>
                </div>

                {/* Right Column: Deep-Dive Technical Details */}
                <div className="modal-info-column">
                  <div className="modal-header-meta">
                    <span
                      className="modal-badge-cat"
                      style={{
                        color: project.color,
                        borderColor: `${project.color}50`,
                        backgroundColor: `${project.color}15`,
                      }}
                    >
                      {project.categoryLabel} • {project.platform}
                    </span>
                    <span className="modal-badge-status">
                      <span className="status-dot"></span> {project.badge}
                    </span>
                  </div>

                  <h2 className="modal-title">{project.title}</h2>
                  <p className="modal-subtitle">{project.subtitle}</p>

                  {project.client && (
                    <div className="modal-client-tag">
                      <FaUserCheck className="client-icon" /> Client: <strong>{project.client}</strong>
                    </div>
                  )}

                  <div className="modal-section">
                    <h4 className="section-heading">
                      <FaLayerGroup /> Project Overview
                    </h4>
                    <p className="modal-description">{project.longDesc}</p>
                  </div>

                  {/* Key Engineering Highlights */}
                  <div className="modal-section">
                    <h4 className="section-heading">
                      <FaRocket /> Engineering Highlights
                    </h4>
                    <ul className="modal-highlights-list">
                      {project.highlights.map((highlight, index) => (
                        <li key={index}>
                          <FaCheckCircle className="highlight-check" style={{ color: project.color }} />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack Chips */}
                  <div className="modal-section">
                    <h4 className="section-heading">
                      <FaCode /> Technology Stack
                    </h4>
                    <div className="modal-tags-row">
                      {project.tags.map((tag, idx) => (
                        <span key={idx} className="modal-tech-chip">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Links */}
                  <div className="modal-actions-footer">
                    {project.links.appStore && (
                      <a
                        href={project.links.appStore}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="store-action-btn appstore"
                      >
                        <FaApple className="store-btn-icon" />
                        <div className="store-btn-text">
                          <small>Download on the</small>
                          <strong>App Store</strong>
                        </div>
                      </a>
                    )}

                    {project.links.playStore && (
                      <a
                        href={project.links.playStore}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="store-action-btn playstore"
                      >
                        <FaGooglePlay className="store-btn-icon" />
                        <div className="store-btn-text">
                          <small>Get it on</small>
                          <strong>Google Play</strong>
                        </div>
                      </a>
                    )}

                    {project.links.web && (
                      <a
                        href={project.links.web}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="store-action-btn web"
                      >
                        <FaGlobe className="store-btn-icon" />
                        <div className="store-btn-text">
                          <small>Visit Platform</small>
                          <strong>Live Website</strong>
                        </div>
                      </a>
                    )}

                    {project.links.github && (
                      <a
                        href={project.links.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="store-action-btn github"
                      >
                        <FaGithub className="store-btn-icon" />
                        <div className="store-btn-text">
                          <small>View Code</small>
                          <strong>GitHub Repository</strong>
                        </div>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
